import type { OutgoingIntakeFormNode } from "../../api/types/outgoingIntake";

/**
 * Shared logic for turning an intake schema into the ordered list of
 * "Typeform steps" (one screen per step). This is the single source of truth
 * used by both the public intake preview (`PublicOutgoingIntake`) and the
 * builder's left rail, so the rail's item order/numbering matches the preview
 * step order exactly.
 */

export function isDocumentsSectionNode(
  node: OutgoingIntakeFormNode | null | undefined
): boolean {
  if (!node || node.node_type !== "section") return false;
  const titleNormalized = String(node.title ?? "")
    .trim()
    .toLowerCase();
  return (
    titleNormalized === "documents" ||
    titleNormalized === "document" ||
    titleNormalized.includes("documents") ||
    node.node_id === "documents" ||
    node.node_id === "documents_section"
  );
}

/**
 * Flatten a schema into the ordered list of step nodes.
 *
 * - Every top-level node is a base step, in schema order: a question beside a
 *   section is still a step (the server's `flatten_steps` agrees).
 * - Non-document sections are expanded so each child question becomes a step.
 * - Document sections promote their direct file questions to individual steps,
 *   then expand any remaining children.
 */
export function flattenIntakeStepNodes(
  schema: OutgoingIntakeFormNode[]
): OutgoingIntakeFormNode[] {
  const baseSteps = (schema ?? []).filter(Boolean);

  const expandSectionIntoTypeformSteps = (
    node: OutgoingIntakeFormNode
  ): OutgoingIntakeFormNode[] => {
    if (node.node_type !== "section") return [node];

    const directChildren = (node.children ?? []).filter(Boolean);

    if (!isDocumentsSectionNode(node)) {
      return directChildren.length
        ? directChildren.flatMap(expandSectionIntoTypeformSteps)
        : [node];
    }

    const directFileQuestions = directChildren.filter(
      (child) =>
        child.node_type === "question" && child.question_type === "file"
    );

    if (directFileQuestions.length === 0) {
      return directChildren.length
        ? directChildren.flatMap(expandSectionIntoTypeformSteps)
        : [node];
    }

    const otherChildren = directChildren.filter(
      (child) =>
        !(child.node_type === "question" && child.question_type === "file")
    );

    return [
      ...directFileQuestions,
      ...otherChildren.flatMap(expandSectionIntoTypeformSteps)
    ];
  };

  return baseSteps.flatMap(expandSectionIntoTypeformSteps);
}

/**
 * Return the index of the step whose `node_id` matches, or -1 if not present.
 */
export function findStepIndexForNode(
  schema: OutgoingIntakeFormNode[],
  nodeId: string | null | undefined
): number {
  const target = String(nodeId ?? "").trim();
  if (!target) return -1;
  const steps = flattenIntakeStepNodes(schema);
  return steps.findIndex(
    (step) => String(step.node_id ?? "").trim() === target
  );
}
